
import React, { useState, useEffect } from "react";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Loader2 } from "lucide-react";
import { galleryService, GalleryPhoto } from "@/services/galleryService";
import { toast } from "@/hooks/use-toast";

interface DynamicImageGalleryProps {
  gallerySlug: string;
  fallbackImages?: Array<{
    src: string;
    description: string;
  }>;
}

export const DynamicImageGallery: React.FC<DynamicImageGalleryProps> = ({ 
  gallerySlug, 
  fallbackImages = [] 
}) => {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadGalleryPhotos = async () => {
      try {
        setLoading(true);
        setError(null);
        const galleryPhotos = await galleryService.getGalleryPhotos(gallerySlug);
        setPhotos(galleryPhotos);
      } catch (err) {
        console.error('Failed to load gallery photos:', err);
        setError('Failed to load gallery photos');
        toast({
          title: "Gallery Error",
          description: "Failed to load photos. Using fallback images.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadGalleryPhotos();
  }, [gallerySlug]);

  // Use fallback images if no photos loaded or if there was an error
  const displayImages = photos.length > 0 ? photos.map(photo => ({
    src: photo.file_path,
    description: photo.alt_text || photo.description || photo.title
  })) : fallbackImages;

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-tribal-primary" />
        <span className="ml-2 text-tribal-secondary">Loading gallery...</span>
      </div>
    );
  }

  if (displayImages.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto text-center py-8">
        <p className="text-tribal-secondary">No photos available in this gallery.</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <Carousel className="w-full max-w-4xl mx-auto">
        <CarouselContent>
          {displayImages.map((image, index) => (
            <CarouselItem key={index}>
              <div className="p-1">
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg">
                  <img
                    src={image.src}
                    alt={image.description}
                    className="object-cover w-full h-full"
                    onError={(e) => {
                      console.log(`Failed to load image: ${image.src}`);
                      // You can set a fallback image here if needed
                    }}
                  />
                </div>
                <p className="mt-2 text-center text-sm text-tribal-secondary">
                  {image.description}
                </p>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="hidden md:flex" />
        <CarouselNext className="hidden md:flex" />
      </Carousel>
      
      {error && (
        <p className="text-center text-sm text-red-600">
          {error} - Showing fallback images.
        </p>
      )}
    </div>
  );
};
